/**
 * Fetch a narrative via a share token from the GraphQL API.
 *
 * The share token encodes access to a specific narrative. Dispatch returns the
 * full narrative JSON (`narrativeContentJson`) plus a `sessionId` — the
 * credential the viewer replays on later loads (see `shareTokenSession.ts`).
 *
 * Access goes through `shareTokenAccess`, a non-null envelope that reports
 * *why* a request was denied. Its predecessor `shareToken` returned a bare
 * nullable token, so "expired", "already claimed", and "no such token" all
 * arrived as the same `null` and the viewer could only ever say "Report Not
 * Found". `shareToken` is kept as a one-shot fallback: the two repos deploy on
 * independent rollouts, and a Dispatch rollback must degrade rather than black
 * out every share link.
 *
 * Usage: ?share=aac69dfb-d34c-407a-a586-0ff4b98a9a92
 */

import type {
  NarrativeEntry,
  NarrativeWithDataValues,
  PipelineManifest,
  PipelineManifestEntry,
} from "../narratives";
import type { NewNarrativeData, DataValues } from "../types/narrative";
import {
  validateNarrativeLenient,
  formatValidationErrors,
} from "@juiceforward/narrative-schema/validation";

// Unauthenticated queries (shareTokenAccess, recordSessionAction) go through
// the proxy to avoid CORS entirely. This path must stay same-origin and must
// never set `credentials` — the share flow is unauthenticated by design. The
// one exception is the reviewer channel (DISPATCH_SESSION_CHANNEL below).
const envApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
const API_URL = envApiUrl || "/api/graphql";

if (
  import.meta.env.DEV &&
  envApiUrl &&
  /^[a-z][a-z0-9+.-]*:\/\//i.test(envApiUrl)
) {
  console.warn(
    `[Forward] VITE_API_URL is set to an absolute URL (${envApiUrl}). ` +
      "This takes the share-token path cross-origin, bypassing the nginx " +
      "proxy that keeps it same-origin. Expect CORS failures and preflights " +
      "that production will not have. Leave it unset to use /api/graphql.",
  );
}

// Authenticated queries (campaignRuns, campaignRunManifest) must hit dispatch
// directly so the browser sends the session cookie. The proxy can't forward
// cookies that belong to the dispatch domain.
const envDispatchUrl = (
  import.meta.env.VITE_DISPATCH_URL as string | undefined
)?.trim();
const DEFAULT_DISPATCH_URL = "https://dispatch.dev.insightsrecap.com/graphql/";
const AUTHED_API_URL = envDispatchUrl || DEFAULT_DISPATCH_URL;

/**
 * Where a share-path document is posted, and with what.
 *
 * The recipient channel is the same-origin proxy with no credentials — the
 * share flow is unauthenticated by design (ADR-0006). The reviewer channel is
 * the exception: Dispatch resolves its claim exemption for signed-in workspace
 * members from the request's own authenticated identity, and that identity can
 * only travel as Dispatch's session cookie, which is host-only to the Dispatch
 * host. The browser never attaches it to a request on the viewer origin, so
 * through the proxy every reviewer is a stranger, and a bound link opened for
 * QA is claimed exactly as a leaked one would be.
 */
type ShareChannel = { url: string; credentials?: RequestCredentials };
const PROXY_CHANNEL: ShareChannel = { url: API_URL };
const DISPATCH_SESSION_CHANNEL: ShareChannel = {
  url: AUTHED_API_URL,
  credentials: "include",
};

export interface ShareTokenResult {
  entry: NarrativeEntry;
  sessionId: string;
}

/**
 * Every way a share-token load can end.
 *
 * The previous shape was `ShareTokenResult | null`, which collapsed an HTTP
 * failure, a denied token, a malformed payload, a schema-validation failure and
 * a thrown network error into one indistinguishable `null`. The recovery UX
 * hangs off this union: `expired` needs renewal, `verificationRequired` needs
 * the recipient to re-prove the link is theirs, and `networkError` needs a
 * retry — three different screens behind what used to be one.
 *
 * `networkError` is the non-access arm: transport failure, a non-200, GraphQL
 * `errors[]`, or a granted payload the narrative schema rejects. It means
 * "something went wrong", never "you may not see this".
 */
export type ShareTokenAccessResult =
  | ({ kind: "ok" } & ShareTokenResult)
  | { kind: "expired" }
  | { kind: "verificationRequired" }
  | { kind: "tierUnsupported" }
  | { kind: "networkError" };

const SHARE_TOKEN_ACCESS_QUERY = `query ShareTokenAccess($token: UUID!, $sessionId: UUID) {
  shareTokenAccess(token: $token, sessionId: $sessionId) {
    outcome
    shareToken {
      sessionId
      narrativeContentJson
    }
  }
}`;

/**
 * The pre-envelope field, kept only as a one-shot fallback for a Dispatch
 * rollback. GraphQL validates a whole document against the schema, so a viewer
 * that selects `shareTokenAccess` against a Dispatch that no longer declares it
 * gets nothing back at all — this query is what turns that blackout into
 * today's behavior.
 */
const LEGACY_SHARE_TOKEN_QUERY = `query ShareToken($token: UUID!) {
  shareToken(token: $token) {
    sessionId
    narrativeContentJson
  }
}`;

export interface FetchShareTokenOptions {
  /**
   * Post the share query straight to Dispatch with the browser's Dispatch
   * session, instead of through the same-origin proxy. For the internal review
   * flow (`?devnav`) only: it is how a signed-in Juicer reaches Dispatch's
   * claim exemption rather than consuming a recipient's one claim. Falls back
   * to the proxy, with a warning, if Dispatch cannot be reached directly (CORS,
   * network) — that fallback opens the link as an anonymous visitor would.
   */
  viaDispatchSession?: boolean;
}

/**
 * Requests in flight, keyed by access context: token, the session being
 * replayed, and the channel. Two mounts asking the same question at once share
 * one request. In dev, StrictMode double-invokes the mount effect, and two
 * racing requests for an unclaimed bound token means one of them claims it
 * while the other is denied — and it is a coin flip which result the mount that
 * survives receives. Coalescing them makes the first open of a bound link
 * deterministic, whichever effect run ends up rendering it.
 *
 * The key is the whole context, not the token alone, so a recipient-channel
 * request and a reviewer-channel request for one token never share a result,
 * and neither do two callers replaying different credentials.
 */
const inFlight = new Map<string, Promise<ShareTokenAccessResult>>();

function inFlightKey(
  token: string,
  sessionId: string | null | undefined,
  options: FetchShareTokenOptions,
): string {
  // Same normalization the loader applies, so "" and undefined key together.
  return JSON.stringify([
    token,
    sessionId || null,
    options.viaDispatchSession === true,
  ]);
}

/**
 * Fetch a narrative by share token, replaying `sessionId` if the viewer has one
 * stored for this token.
 *
 * On a bound token `sessionId` is the credential: replaying a session Dispatch
 * itself issued proves this browser is the claimer. Callers must persist the
 * `sessionId` of every `ok` result, including one they no longer need because
 * the component that asked has unmounted — that result may be the claim, and a
 * claim whose credential nobody stored is a link nobody can open.
 */
export function fetchShareTokenNarrative(
  token: string,
  sessionId?: string | null,
  options: FetchShareTokenOptions = {},
): Promise<ShareTokenAccessResult> {
  const key = inFlightKey(token, sessionId, options);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request: Promise<ShareTokenAccessResult> = loadShareTokenNarrative(
    token,
    sessionId,
    options,
  ).finally(() => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
}

async function loadShareTokenNarrative(
  token: string,
  sessionId: string | null | undefined,
  options: FetchShareTokenOptions,
): Promise<ShareTokenAccessResult> {
  const variables = { token, sessionId: sessionId || null };
  let channel = options.viaDispatchSession
    ? DISPATCH_SESSION_CHANNEL
    : PROXY_CHANNEL;
  let reply = await postShareTokenQuery(
    SHARE_TOKEN_ACCESS_QUERY,
    variables,
    channel,
  );

  if (reply.transportFailed && channel !== PROXY_CHANNEL) {
    console.warn(
      "[Forward] Could not reach Dispatch directly for the reviewer share " +
        "path; falling back to the same-origin proxy. A bound token opened " +
        "this way is claimed as an anonymous visitor would claim it.",
    );
    channel = PROXY_CHANNEL;
    reply = await postShareTokenQuery(
      SHARE_TOKEN_ACCESS_QUERY,
      variables,
      channel,
    );
  }

  if (reply.transportFailed) return { kind: "networkError" };

  // A Dispatch that predates (or has rolled back past) the envelope field
  // rejects the document at validation time. Retry once with the old shape
  // rather than showing "Report Not Found" for every live share link.
  if (mentionsUnknownAccessField(reply.errors)) {
    console.warn(
      "[Forward] shareTokenAccess is not in this Dispatch schema; " +
        "falling back to the legacy shareToken query.",
    );
    return fetchLegacyShareTokenNarrative(token, channel);
  }

  if (!reply.ok) {
    console.warn(`[Forward] Share token API returned ${reply.status}`);
    return { kind: "networkError" };
  }

  if (reply.errors.length > 0) {
    console.warn("[Forward] shareTokenAccess returned errors:", reply.errors);
    return { kind: "networkError" };
  }

  const envelope = reply.data?.shareTokenAccess as
    ShareTokenAccessEnvelope | null | undefined;

  if (!envelope || typeof envelope.outcome !== "string") {
    console.warn(
      "[Forward] shareTokenAccess response carried no outcome",
      reply.data,
    );
    return { kind: "networkError" };
  }

  switch (envelope.outcome.toUpperCase()) {
    case "GRANTED": {
      const built = buildResult(envelope.shareToken);
      if (!built) return { kind: "networkError" };
      return { kind: "ok", ...built };
    }
    case "EXPIRED":
      return { kind: "expired" };
    case "VERIFICATION_REQUIRED":
      return { kind: "verificationRequired" };
    case "TIER_UNSUPPORTED":
      return { kind: "tierUnsupported" };
    default:
      // An outcome this build predates. Fail closed — we have no narrative to
      // render either way, and routing to the access gate is the honest
      // reading of "the server declined for a reason I don't understand".
      console.warn(
        `[Forward] Unrecognized shareTokenAccess outcome: ${envelope.outcome}`,
      );
      return { kind: "tierUnsupported" };
  }
}

/** The rollback path: the legacy field can only say yes or null. */
async function fetchLegacyShareTokenNarrative(
  token: string,
  channel: ShareChannel,
): Promise<ShareTokenAccessResult> {
  const reply = await postShareTokenQuery(
    LEGACY_SHARE_TOKEN_QUERY,
    { token },
    channel,
  );

  if (reply.transportFailed) return { kind: "networkError" };

  if (!reply.ok) {
    console.warn(`[Forward] Share token API returned ${reply.status}`);
    return { kind: "networkError" };
  }

  if (reply.errors.length > 0) {
    console.warn("[Forward] shareToken returned errors:", reply.errors);
    return { kind: "networkError" };
  }

  const payload = reply.data?.shareToken as
    ShareTokenPayload | null | undefined;

  if (!payload?.narrativeContentJson) {
    // The legacy field reports every denial as null and cannot say which one.
    // `verificationRequired` is the outcome Dispatch itself assigns to a token
    // it cannot serve without knowing more, and it maps to the same screen this
    // path has always shown.
    console.warn(
      "[Forward] Share token response missing narrativeContentJson",
      reply.data,
    );
    return { kind: "verificationRequired" };
  }

  const built = buildResult(payload);
  return built ? { kind: "ok", ...built } : { kind: "networkError" };
}

type ShareTokenPayload = {
  sessionId?: string | null;
  narrativeContentJson?: NarrativeWithDataValues | null;
};

type ShareTokenAccessEnvelope = {
  outcome?: string | null;
  shareToken?: ShareTokenPayload | null;
};

type GraphQLErrorLike = { message?: string };

type GraphQLReply = {
  /** True only when `fetch` itself threw — no HTTP exchange happened. */
  transportFailed: boolean;
  ok: boolean;
  status: number;
  data: Record<string, unknown> | null;
  /** Always an array, so callers never null-guard before inspecting it. */
  errors: GraphQLErrorLike[];
};

/**
 * POST a share-path GraphQL document over `channel`.
 *
 * The recipient channel sets no `credentials` at all: the share path is
 * unauthenticated and must stay same-origin through the nginx proxy. Only the
 * reviewer channel carries the Dispatch session. The body is parsed even on a
 * non-2xx, because GraphQL validation failures can arrive with a 400 and the
 * `errors[]` inside them are what tell a rollback apart from an outage.
 */
async function postShareTokenQuery(
  query: string,
  variables: Record<string, unknown>,
  channel: ShareChannel = PROXY_CHANNEL,
): Promise<GraphQLReply> {
  let response: Response;
  try {
    response = await fetch(channel.url, {
      method: "POST",
      ...(channel.credentials ? { credentials: channel.credentials } : {}),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables }),
    });
  } catch (err) {
    console.warn("[Forward] fetchShareTokenNarrative: network error:", err);
    return {
      transportFailed: true,
      ok: false,
      status: 0,
      data: null,
      errors: [],
    };
  }

  let json: Record<string, unknown> | null = null;
  try {
    json = (await response.json()) as Record<string, unknown> | null;
  } catch {
    // A body that isn't JSON (an HTML error page from the proxy, say) is a
    // transport-shaped failure, not an access decision.
    json = null;
  }

  const rawErrors = json?.errors;
  return {
    transportFailed: false,
    ok: response.ok,
    status: response.status,
    data: (json?.data as Record<string, unknown> | null) ?? null,
    errors: Array.isArray(rawErrors) ? (rawErrors as GraphQLErrorLike[]) : [],
  };
}

/**
 * Whether `errors` is GraphQL telling us `shareTokenAccess` (or its `sessionId`
 * argument) isn't in the schema — the signature of a Dispatch rollback, as
 * opposed to a runtime failure inside a field that does exist.
 */
function mentionsUnknownAccessField(errors: GraphQLErrorLike[]): boolean {
  return errors.some((err) => {
    const message = typeof err?.message === "string" ? err.message : "";
    if (!message.includes("shareTokenAccess")) return false;
    return /cannot query field|unknown argument|unknown field|unknown type/i.test(
      message,
    );
  });
}

function buildResult(
  data: ShareTokenPayload | null | undefined,
): ShareTokenResult | null {
  const narrativeJson = data?.narrativeContentJson;
  if (!narrativeJson) {
    console.warn(
      "[Forward] Share token grant carried no narrativeContentJson",
      data,
    );
    return null;
  }

  const result = validateNarrativeLenient(narrativeJson);

  if (result.warnings.length > 0) {
    console.warn(
      "[Forward] Sanitized unrecognized config in share token narrative:",
    );
    result.warnings.forEach((w) => console.warn(`  • ${w}`));
  }

  if (!result.success) {
    console.warn(
      "[Forward] Schema validation failed for share token narrative:",
    );
    console.warn(formatValidationErrors(result.error));
    return null;
  }

  const { narrativeDataValues, ...narrativeData } = result.data;
  return {
    entry: {
      narrative: narrativeData as NewNarrativeData,
      dataValues: narrativeDataValues as unknown as DataValues,
    },
    sessionId: data?.sessionId ?? "",
  };
}

export function toNumericId(raw: unknown): number | null {
  if (raw == null) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Record a user interaction against a session.
 * Fire-and-forget — errors are logged in dev but never block the UI.
 */
export function recordSessionAction(
  sessionId: string,
  actionType: string,
  metadata: Record<string, unknown>,
): void {
  if (!sessionId) return;

  fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation RecordSessionAction($sessionId: UUID!, $actionType: String!, $metadata: JSON) {
  recordSessionAction(sessionId: $sessionId, actionType: $actionType, metadata: $metadata) {
    success
    message
  }
}`,
      variables: { sessionId, actionType, metadata },
    }),
  }).catch((err) => {
    if (import.meta.env.DEV) {
      console.warn("⚠️ recordSessionAction: error:", err);
    }
  });
}

// ---------------------------------------------------------------------------
// Campaign run queries (used by the devnav Review Narratives panel when the
// narrative is loaded via a share token).
// ---------------------------------------------------------------------------

export interface CampaignRunInfo {
  id: number;
  name: string;
  /** ISO timestamp of when the run was created, when the server provides it. */
  created?: string;
  /** ISO timestamp after which none of the run's share links open (the
   * earlier of the campaign's expiry and its latest token expiry). */
  expiresAt?: string;
  /** True when the run's share links have all lapsed. Undefined when the
   * server predates the field. */
  isExpired?: boolean;
  /** The workspace the run's campaign belongs to. Undefined when the server
   * predates the field. */
  workspaceId?: number;
  workspaceName?: string;
  /** "personal" or "shared". */
  workspaceKind?: string;
}

export interface WorkspaceOption {
  /** Stringified workspace id, used as the <select> value. */
  id: string;
  label: string;
}

/**
 * The distinct workspaces among `runs`, sorted by name. Empty when the server
 * doesn't report workspaces, in which case the filter is not shown.
 */
export function workspaceOptions(runs: CampaignRunInfo[]): WorkspaceOption[] {
  const byId = new Map<number, string>();
  for (const run of runs) {
    if (run.workspaceId == null) continue;
    if (!byId.has(run.workspaceId)) {
      byId.set(
        run.workspaceId,
        run.workspaceName || `Workspace ${run.workspaceId}`,
      );
    }
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id: String(id), label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Format an ISO timestamp as a short local date, or null if unparseable. */
export function formatRunDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Human labels for the run switcher, keyed by run id.
 *
 * Run names are not unique — every workspace has a "Standalone Narratives"
 * run, and QA runs get reused names — so a bare name can leave several
 * indistinguishable options. Colliding names are qualified in stages, each
 * applied only to labels that still collide: the workspace name first (the
 * usual reason for a collision), then the creation date, then the run id,
 * which is unique by construction. Pass only the runs actually shown so a
 * list already filtered to one workspace doesn't repeat that workspace.
 */
export function labelCampaignRuns(
  runs: CampaignRunInfo[],
): Map<number, string> {
  const labels = new Map<number, string>(runs.map((r) => [r.id, r.name]));
  const qualifiers: Array<(run: CampaignRunInfo) => string | null> = [
    (run) => run.workspaceName || null,
    (run) => formatRunDate(run.created),
    (run) => `#${run.id}`,
  ];

  for (const qualify of qualifiers) {
    const groups = new Map<string, CampaignRunInfo[]>();
    for (const run of runs) {
      const label = labels.get(run.id)!;
      groups.set(label, [...(groups.get(label) ?? []), run]);
    }
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      for (const run of group) {
        const qualifier = qualify(run);
        if (qualifier)
          labels.set(run.id, `${labels.get(run.id)} · ${qualifier}`);
      }
    }
  }

  // Expiry is appended last so it never takes part in disambiguation.
  for (const run of runs) {
    if (run.isExpired) labels.set(run.id, `${labels.get(run.id)} (expired)`);
  }
  return labels;
}

/**
 * Order runs for the switcher: reviewable runs first, expired ones last,
 * server order preserved within each group.
 */
export function sortCampaignRuns(runs: CampaignRunInfo[]): CampaignRunInfo[] {
  return [
    ...runs.filter((r) => !r.isExpired),
    ...runs.filter((r) => r.isExpired),
  ];
}

export interface DispatchManifestEntry {
  customerId: string;
  customerName: string;
  status: string;
  flagged: boolean;
  shareTokens: string[];
  sectionCount: number;
  groupCount: number;
  flagReasons?: string[];
  sectionGroups?: Record<string, string[]>;
}

export interface DispatchManifest {
  runId: string;
  generatedAt?: string;
  entries: DispatchManifestEntry[];
}

// Dispatch and Forward deploy independently. If this build reaches a Dispatch
// that predates `expiresAt` / `isExpired`, GraphQL rejects the whole document
// ("Cannot query field …"), so the legacy selection is retried in that case
// rather than showing an empty run list until the backend catches up.
const CAMPAIGN_RUNS_QUERY = `query CampaignRuns {
  campaignRuns {
    id
    name
    created
    expiresAt
    isExpired
    workspaceId
    workspaceName
    workspaceKind
  }
}`;
const LEGACY_CAMPAIGN_RUNS_QUERY = `query CampaignRuns {
  campaignRuns {
    id
    name
    created
  }
}`;

function parseCampaignRun(r: Record<string, unknown>): CampaignRunInfo | null {
  const id = toNumericId(r.id);
  if (id == null) return null;
  return {
    id,
    name: typeof r.name === "string" && r.name ? r.name : String(id),
    created: typeof r.created === "string" ? r.created : undefined,
    expiresAt: typeof r.expiresAt === "string" ? r.expiresAt : undefined,
    isExpired: typeof r.isExpired === "boolean" ? r.isExpired : undefined,
    workspaceId: toNumericId(r.workspaceId) ?? undefined,
    workspaceName:
      typeof r.workspaceName === "string" && r.workspaceName
        ? r.workspaceName
        : undefined,
    workspaceKind:
      typeof r.workspaceKind === "string" && r.workspaceKind
        ? r.workspaceKind
        : undefined,
  };
}

/**
 * Outcome of listing campaign runs. `unauthenticated` is split out from other
 * failures because the viewer acts on it: a `?devnav` link opened without a
 * Dispatch session drops the review flow rather than showing an empty panel.
 * An outage is not a reason to do that, so it stays a plain `error`.
 */
export type CampaignRunsResult =
  | { kind: "ok"; runs: CampaignRunInfo[] }
  | { kind: "unauthenticated" }
  | { kind: "error" };

/**
 * Fetch the list of campaign runs visible to the authenticated user.
 */
export async function fetchCampaignRuns(): Promise<CampaignRunsResult> {
  try {
    let response = await postCampaignRunsQuery(CAMPAIGN_RUNS_QUERY);
    if (response.body && hasUnknownFieldError(response.body)) {
      response = await postCampaignRunsQuery(LEGACY_CAMPAIGN_RUNS_QUERY);
    }
    const { status, body } = response;
    if (status === 401 || status === 403 || (body && hasAuthError(body))) {
      return { kind: "unauthenticated" };
    }
    const runs = body?.data?.campaignRuns;
    if (!Array.isArray(runs)) return { kind: "error" };
    return {
      kind: "ok",
      runs: runs
        .map(parseCampaignRun)
        .filter((r): r is CampaignRunInfo => r !== null),
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("⚠️ fetchCampaignRuns: error:", err);
    }
    return { kind: "error" };
  }
}

type CampaignRunsBody = {
  data?: { campaignRuns?: unknown };
  errors?: Array<{ message?: unknown }>;
};

async function postCampaignRunsQuery(
  query: string,
): Promise<{ status: number; body: CampaignRunsBody | null }> {
  const response = await fetch(AUTHED_API_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  if (!response.ok) return { status: response.status, body: null };
  return {
    status: response.status,
    body: (await response.json()) as CampaignRunsBody,
  };
}

function hasErrorMatching(body: CampaignRunsBody, pattern: RegExp): boolean {
  return (
    Array.isArray(body.errors) &&
    body.errors.some(
      (e) => typeof e.message === "string" && pattern.test(e.message),
    )
  );
}

function hasUnknownFieldError(body: CampaignRunsBody): boolean {
  return hasErrorMatching(body, /cannot query field/i);
}

// Dispatch answers 200 with this GraphQL error when no session is present.
function hasAuthError(body: CampaignRunsBody): boolean {
  return hasErrorMatching(body, /authentication required/i);
}

/**
 * Outcome of a manifest fetch. Every failure carries a short, user-facing
 * message: the review panel shows it in place of a silently empty run list,
 * which is how a production 502 went undiagnosed as "0 narratives".
 */
export type CampaignRunManifestResult =
  | { kind: "ok"; manifest: DispatchManifest }
  | { kind: "error"; message: string };

/**
 * A user-facing description of a non-2xx response from Dispatch. Granularity is
 * deliberately coarse — what the reviewer can do about it, not the HTTP spec:
 * gateway errors mean "the server fell over, retry"; auth codes mean "sign in";
 * everything else just names the status so it can be reported.
 */
export function describeManifestHttpFailure(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return `Dispatch is unavailable right now (HTTP ${status}). Try again in a moment.`;
  }
  if (status === 401 || status === 403) {
    return `Dispatch refused the request (HTTP ${status}). Sign in to Dispatch and try again.`;
  }
  if (status === 429) {
    return "Dispatch rate limit reached (HTTP 429). Wait a minute, then retry.";
  }
  return `Dispatch returned HTTP ${status}.`;
}

/**
 * A user-facing description of a GraphQL-level error. Dispatch answers 200 for
 * these, so they are the path an expired admin session actually takes.
 */
function describeManifestGraphqlError(message: string): string {
  if (/authentication required/i.test(message)) {
    return "Sign in to Dispatch to review this run.";
  }
  return `Dispatch error: ${message}`;
}

function manifestFailure(
  message: string,
  detail?: unknown,
): CampaignRunManifestResult {
  if (import.meta.env.DEV) {
    console.warn("⚠️ fetchCampaignRunManifest:", message, detail ?? "");
  }
  return { kind: "error", message };
}

/**
 * Fetch the manifest for a specific campaign run. The server returns a JSON
 * blob with `runId`, `generatedAt`, and `entries` (each with `shareTokens`).
 *
 * Never throws: every failure mode resolves to an `error` result whose
 * message is suitable to show in the review panel.
 */
export async function fetchCampaignRunManifest(
  campaignRunId: number,
): Promise<CampaignRunManifestResult> {
  let response: Response;
  try {
    response = await fetch(AUTHED_API_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `query CampaignRunManifest($campaignRunId: Int!) {
  campaignRunManifest(campaignRunId: $campaignRunId)
}`,
        variables: { campaignRunId },
      }),
    });
  } catch (err) {
    return manifestFailure(
      "Couldn't reach Dispatch. Check your connection and that you're signed in, then retry.",
      err,
    );
  }
  if (!response.ok) {
    return manifestFailure(describeManifestHttpFailure(response.status), {
      status: response.status,
    });
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (err) {
    return manifestFailure("Dispatch returned an unreadable response.", err);
  }
  const body = (json ?? {}) as {
    data?: { campaignRunManifest?: unknown };
    errors?: Array<{ message?: unknown }>;
  };
  const graphqlError = Array.isArray(body.errors) ? body.errors[0] : undefined;
  if (graphqlError) {
    const message =
      typeof graphqlError.message === "string" && graphqlError.message
        ? graphqlError.message
        : "unknown error";
    return manifestFailure(describeManifestGraphqlError(message), body.errors);
  }

  const manifest = body.data?.campaignRunManifest as
    Record<string, unknown> | null | undefined;
  if (manifest == null) {
    // The resolver answers null for both a missing run and a run in a workspace
    // the viewer doesn't belong to, deliberately, so say both.
    return manifestFailure(
      "Run not found, or your Dispatch account isn't a member of its workspace.",
    );
  }
  if (typeof manifest !== "object") {
    return manifestFailure("Dispatch returned an unexpected response.", json);
  }
  try {
    return {
      kind: "ok",
      manifest: parseDispatchManifest(manifest, campaignRunId),
    };
  } catch (err) {
    return manifestFailure(
      "Dispatch returned a manifest we couldn't read.",
      err,
    );
  }
}

/** Coerce the raw `campaignRunManifest` JSON scalar into a typed manifest. */
function parseDispatchManifest(
  manifest: Record<string, unknown>,
  campaignRunId: number,
): DispatchManifest {
  const entries = Array.isArray(manifest.entries)
    ? manifest.entries
        .map((raw: unknown): DispatchManifestEntry | null => {
          // A malformed element is dropped like an entry without a customerId,
          // rather than throwing and discarding the whole run.
          if (!raw || typeof raw !== "object" || Array.isArray(raw))
            return null;
          const e = raw as Record<string, unknown>;
          const customerId =
            typeof e.customerId === "string" ? e.customerId : null;
          const customerName =
            typeof e.customerName === "string" ? e.customerName : "";
          const status = typeof e.status === "string" ? e.status : "";
          const flagged = e.flagged === true;
          const shareTokens = Array.isArray(e.shareTokens)
            ? e.shareTokens.filter((t): t is string => typeof t === "string")
            : [];
          const sectionCount =
            typeof e.sectionCount === "number" ? e.sectionCount : 0;
          const groupCount =
            typeof e.groupCount === "number" ? e.groupCount : 0;
          const flagReasons = Array.isArray(e.flagReasons)
            ? e.flagReasons.filter((r): r is string => typeof r === "string")
            : undefined;
          // The manifest is an untyped JSON scalar, so validate the values too:
          // DevNavigator maps over each group list during render, outside any
          // error handling, and a non-array value would throw there.
          const sectionGroups =
            e.sectionGroups &&
            typeof e.sectionGroups === "object" &&
            !Array.isArray(e.sectionGroups)
              ? Object.fromEntries(
                  Object.entries(
                    e.sectionGroups as Record<string, unknown>,
                  ).map(([section, groups]) => [
                    section,
                    Array.isArray(groups)
                      ? groups.filter((g): g is string => typeof g === "string")
                      : [],
                  ]),
                )
              : undefined;
          if (!customerId) return null;
          return {
            customerId,
            customerName,
            status,
            flagged,
            shareTokens,
            sectionCount,
            groupCount,
            flagReasons,
            sectionGroups,
          };
        })
        .filter(
          (e: DispatchManifestEntry | null): e is DispatchManifestEntry =>
            e !== null,
        )
    : [];
  return {
    runId:
      typeof manifest.runId === "string"
        ? manifest.runId
        : String(campaignRunId),
    generatedAt:
      typeof manifest.generatedAt === "string"
        ? manifest.generatedAt
        : undefined,
    entries,
  };
}

/**
 * Adapt a dispatch manifest into the shape DevNavigator already understands
 * (`PipelineManifest`). Each entry's first share token is used as the
 * synthetic `metadata.report_id` so the existing navigation wiring applies;
 * the parent detects dispatch mode and routes clicks via `?share=` instead
 * of `?report=`.
 */
export function dispatchManifestToPipeline(
  manifest: DispatchManifest,
): PipelineManifest {
  const entries: PipelineManifestEntry[] = manifest.entries.map((e) => {
    const token = e.shareTokens[0] ?? "";
    const isError = e.status === "error";
    return {
      customerId: e.customerId,
      customerName: e.customerName,
      file: token ? "" : "(error)",
      status: e.status,
      flagged: e.flagged,
      sectionCount: e.sectionCount,
      groupCount: e.groupCount,
      sectionGroups: e.sectionGroups,
      flagReasons: e.flagReasons,
      metadata: token ? { report_id: token } : undefined,
      flagReason: isError ? "error" : undefined,
    };
  });
  return {
    runId: manifest.runId,
    generatedAt: manifest.generatedAt,
    entries,
  };
}
